import type { Draft } from '@reduxjs/toolkit';
import type { VerificationView } from '../../index';
import type { VerificationSliceState } from '../state';

/**
 * Frontend completion-gating rule (spec 005 FR-015, mirrors the legacy `CanCompleteBenefits` spec):
 * **Benefits Complete** may be true only when no blocking condition is set — Invalid Patient Info,
 * Invalid Insurance, Invalid CPT/DRG, Insurance Termed, or COB Issue.
 */
export function canCompleteBenefits(
  view: Pick<VerificationView, 'benefitsStatus' | 'comboOfBenefitsIssue'> | null | undefined,
): boolean {
  if (!view) return false;
  const s = view.benefitsStatus;
  const blocked =
    s.invalidPatient ||
    s.invalidInsurance ||
    s.invalidCodes ||
    s.insuranceTermed ||
    view.comboOfBenefitsIssue.isCobIssue;
  return !blocked;
}

/**
 * Enforces the completion gate after a Benefits-Status or COB-Issue edit: if `complete` is set while
 * a blocking condition is active, clear `complete` (FR-015 — the two states are never simultaneously
 * true).
 */
export function enforceCompletionGate(state: Draft<VerificationSliceState>): void {
  const current = state.current;
  if (!current) return;
  if (current.benefitsStatus.complete && !canCompleteBenefits(current)) {
    current.benefitsStatus.complete = false;
  }
}
